import { Tooltip, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material';
import { DatabaseTable } from '../../../../shared/components/ui/DatabaseTable';
import type { Event } from '../../context/EventContext';
import { Badge } from '../../../../shared/components/ui/Badge';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Can } from '../../../../shared/components/Permission';
import { eventService } from '../../../../shared/services/tenant/eventService';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface EventTableProps {
  events: Event[];
  isLoading: boolean;
  onView: (event: Event) => void;
  page: number;
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
  onDuplicate: (event: Event) => void;
  isArchived?: boolean;
  onRefresh?: () => void;
}

export function EventTable({ events, isLoading, onView,
  page,
  limit,
  total,
  onPageChange,
  onPageSizeChange,
  onDuplicate,
  isArchived = false,
  onRefresh,
}: EventTableProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const handleArchive = async (event: Event) => {
    if (!event.id) return;
    setArchivingId(event.id);
    try {
      if (isArchived) {
        await eventService.unarchive(event.id);
      } else {
        if (event.status === 'open') {
          alert('Não é possível arquivar eventos com status aberto');
          return;
        }
        await eventService.archive(event.id);
      }
      queryClient.invalidateQueries({ queryKey: ['events'] });
      onRefresh?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Erro ao arquivar/desarquivar evento';
      alert(errorMessage);
    } finally {
      setArchivingId(null);
    }
  };

  const columns = [
    { field: 'title', headerName: 'Título', width: 200 },
    { field: 'start_date', headerName: 'Data Início', width: 120 },
    { field: 'end_date', headerName: 'Data Fim', width: 120 },
    {
      field: 'organizer',
      headerName: 'Organizador',
      width: 120,
      renderCell: (params: { row: Event }) => (
        <Typography fontSize={14} color={theme.palette.text.primary}>
          {params.row.organizer?.name || '-'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: { row: Event }) => (
        <Tooltip title={
          params.row.status === 'open' ? 'Aberto' :
            params.row.status === 'closed' ? 'Fechado' : 'Cancelado'
        }>
          <span>
            <Badge color={
              params.row.status === 'open' ? 'success' :
                params.row.status === 'closed' ? 'primary' : 'info'
            }>
              {params.row.status === 'open'
                ? 'Aberto'
                : params.row.status === 'closed'
                  ? 'Fechado'
                  : 'Cancelado'}
            </Badge>
          </span>
        </Tooltip>
      )
    },
    {
      field: 'registration_deadline',
      headerName: 'Inscrições até',
      width: 140,
      renderCell: (params: { row: Event }) => (
        <Typography fontSize={14} color={theme.palette.text.primary}>
          {params.row.registration_deadline
            ? new Date(params.row.registration_deadline).toLocaleDateString('pt-BR')
            : '-'}
        </Typography>
      )
    },
    {
      field: '',
      headerName: 'Ações',
      width: isArchived ? 120 : 120,
      renderCell: (params: { row: Event }) => (
        <>
          <Can roles={['ADMIN']}>
            <Tooltip title={isArchived ? 'Desarquivar' : params.row.status === 'open' ? 'Não é possível arquivar eventos abertos' : 'Arquivar'}>
              <span>
                <IconButton
                  aria-label={isArchived ? 'Desarquivar' : 'Arquivar'}
                  onClick={() => handleArchive(params.row)}
                  disabled={archivingId === params.row.id || (!isArchived && params.row.status === 'open')}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Duplicar">
              <IconButton
                aria-label="Duplicar"
                onClick={() => onDuplicate(params.row)}
                size="small"
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Can>
        </>
      )
    },
  ];

  return (
    <DatabaseTable
      rows={events}
      columns={columns}
      loading={isLoading}
      paginationMode="server"
      page={page}
      pageSizeOptions={[5, 15, 25, 50, 100]}
      pageSize={limit}
      rowCount={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onView={onView}
      hideDelete
      hideEdit

    />
  );
}